import { motion } from "framer-motion";
import recruitenLogo from "@/assets/recruiten-logo.webp";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Search, MessageSquare, TrendingUp, Zap, Users, ShieldCheck,
  ArrowRight, Star, MessageCircle, Globe, BarChart3, Clock
} from "lucide-react";

const recrutenFeatures = [
  { icon: Search, title: "Identifikation", desc: "Bis zu 10 parallele KI-Suchstrategien für maximale Reichweite" },
  { icon: MessageSquare, title: "Ansprache", desc: "Personalisierte Direktansprache auf Basis von Profil & Karriere" },
  { icon: TrendingUp, title: "Performance", desc: "Selbstlernende KI optimiert Response-Rates kontinuierlich" },
  { icon: Zap, title: "Kommunikation", desc: "Echtzeit-Antworten und intelligente Follow-ups" },
  { icon: Users, title: "Interview", desc: "Automatisierte Terminvereinbarung und Vorqualifizierung" },
  { icon: ShieldCheck, title: "DSGVO", desc: "Vollständig in Europa gehostet und DSGVO-konform" },
];

const zentenFeatures = [
  { icon: Zap, title: "Automatisierung", desc: "Vollautomatische KI-Antworten auf Kundenanfragen – präzise, schnell und markenkonform" },
  { icon: Globe, title: "Multi-Plattform", desc: "Amazon, Kaufland, eBay, Mediamarkt und weitere Marktplätze im DACH-Raum aus einer zentralen Lösung verwaltet" },
  { icon: Clock, title: "24/7 Verfügbarkeit", desc: "Kundenanfragen werden rund um die Uhr beantwortet – ohne Wartezeiten, ohne Personalaufwand" },
  { icon: TrendingUp, title: "Performance", desc: "Selbstlernende KI optimiert Antwortqualität und Kundenzufriedenheit kontinuierlich" },
  { icon: BarChart3, title: "Analyse", desc: "Datengetriebene Auswertung der gesamten Kundenkommunikation für bessere Entscheidungen" },
  { icon: ShieldCheck, title: "DSGVO", desc: "Vollständig in Europa gehostet und DSGVO-konform" },
];

const ProductsSection = () => {
  return (
    <section id="produkte" className="py-16 md:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Unsere Produkte
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Zwei KI-Lösungen, entwickelt aus realer unternehmerischer Erfahrung
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* recruiTen.AI */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="h-full border-border bg-card shadow-sm transition-all duration-300 hover:shadow-md hover:border-primary/20">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/5 text-foreground text-xs font-semibold mb-4">
                  <Users className="w-3 h-3 text-primary/60" /> Für Personalberatungen & Recruiting-Agenturen
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <img src={recruitenLogo} alt="recruiTen.AI Logo" className="w-12 h-12 rounded-xl" />
                  <h3 className="text-2xl font-bold text-foreground">recruiTen.AI</h3>
                </div>
                <p className="text-xs text-muted-foreground italic">Entwickelt aus dem Alltag einer aktiven Personalberatung</p>
                <p className="text-muted-foreground mb-1 text-sm font-medium">
                  Das KI-Tool für Personalberater und Headhunter
                </p>
                <p className="text-muted-foreground mb-1 text-xs">
                  Automatisiert Kandidatensuche, Direktansprache und Terminvereinbarung – das beste Tool für automatisiertes Recruiting in Deutschland
                </p>
                <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/5 text-foreground text-xs font-semibold mb-6">
                  <Star className="w-3 h-3 text-primary/60" /> Recruiter werden bis zu 10x effektiver
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {recrutenFeatures.map((f) => (
                    <div key={f.title} className="flex gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/5 flex items-center justify-center shrink-0">
                        <f.icon className="w-4 h-4 text-primary/60" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{f.title}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Button asChild className="rounded-full w-full font-semibold mt-auto bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/20" size="lg">
                  <a href="https://recruten.ai" target="_blank" rel="noopener noreferrer">
                    Mehr erfahren <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* zenTen.AI */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="h-full border-border bg-card shadow-sm transition-all duration-300 hover:shadow-md hover:border-secondary/20">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary/5 text-foreground text-xs font-semibold mb-4">
                  <Globe className="w-3 h-3 text-secondary/60" /> Für E-Commerce & Onlineshop-Betreiber
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-xl bg-secondary/5 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-secondary/60" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">zenTen.AI</h3>
                </div>
                <p className="text-xs text-muted-foreground italic">Entwickelt aus dem Alltag eines echten Marktplatz-Händlers</p>
                <p className="text-muted-foreground mb-1 text-sm font-medium">
                  KI für automatische Kundenantworten im E-Commerce
                </p>
                <p className="text-muted-foreground mb-1 text-xs">
                  Automatische Antworten auf Kundenanfragen bei Amazon, Kaufland, eBay und weiteren Marktplätzen – Kundenservice Automatisierung für Onlineshops
                </p>
                <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-secondary/5 text-foreground text-xs font-semibold mb-6">
                  <Star className="w-3 h-3 text-secondary/60" /> Händler werden bis zu 10x effizienter im Kundenservice
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {zentenFeatures.map((f) => (
                    <div key={f.title} className="flex gap-3">
                      <div className="w-9 h-9 rounded-lg bg-secondary/5 flex items-center justify-center shrink-0">
                        <f.icon className="w-4 h-4 text-secondary/60" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{f.title}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  asChild
                  className="rounded-full w-full font-semibold mt-auto bg-[#333333] text-white hover:bg-[#444444]"
                  size="lg"
                >
                  <a href="https://zenten.ai" target="_blank" rel="noopener noreferrer">
                    Mehr erfahren <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
